/**
 * Cross-app deep links from the supervision dashboard (2026-07 request: tax-appointment rows should
 * link out to the client's tax work and to their reviews).
 *
 * ## Why these are LIST links, not record links
 *
 * ptc-tax-lifecycle stores the Dataverse `activityid` on the tax return it generates for an
 * appointment (`TaxReturn.appointmentId`, also its Cosmos partition key), so the appointment→return
 * mapping definitely exists — but only SERVER-side. Its only record route is `/tax-returns/:id`
 * keyed by the return's OWN id, and resolving `activityid → return id` needs an authenticated call
 * to its `GET /api/tax-returns?appointmentId=...` endpoint (a different app registration's
 * `access_as_user` scope). One appointment can also legitimately fan out to several returns (one per
 * attendee: spouse, business). Rather than invent a URL we can't guarantee, we link to that app's
 * returns LIST pre-filtered to the client — a URL grammar read straight out of its source
 * (`web-app/src/components/listFilters/useListSearchParams.ts` +
 * `TaxReturnsPage.tsx`'s `FILTER_FIELDS`/`PRESET_VIEWS`), so it lands filtered instead of guessing.
 *
 * `view=all` is REQUIRED: tax-lifecycle's default view is `my-returns` (scoped to the signed-in
 * preparer), which would hide a supervisor's clients. `clientName` is the one client-identifying
 * column in its filter allowlist, and its filter engine matches text case-insensitively; the value
 * we pass is the Dataverse account name, which is exactly what its appointment sync writes to
 * `TaxReturn.clientName` for account-linked appointments (`AppointmentSyncFunctions.cs`).
 */

/**
 * ptc-tax-lifecycle's staff SWA host (verified live 2026-07-25). Same constant the tax app itself
 * uses to build outbound links: `api/Services/NotificationService.cs` `DefaultDashboardUrl`.
 */
const DEFAULT_TAX_LIFECYCLE_BASE_URL = 'https://red-ocean-08005b40f.2.azurestaticapps.net';

/**
 * Normalises an env-provided base URL. An unset `VITE_*` var can reach runtime as the literal
 * string "undefined" (this cost ptc-statetax an outage), so anything that isn't an absolute http(s)
 * URL falls back to the known-good default. Trailing slashes are trimmed.
 */
export function resolveTaxLifecycleBaseUrl(raw?: string | null): string {
  const candidate = (raw ?? '').trim();
  if (!/^https?:\/\/\S+$/i.test(candidate) || candidate === 'undefined') {
    return DEFAULT_TAX_LIFECYCLE_BASE_URL;
  }
  return candidate.replace(/\/+$/, '');
}

export const TAX_LIFECYCLE_BASE_URL = resolveTaxLifecycleBaseUrl(
  import.meta.env.VITE_TAX_LIFECYCLE_URL
);

/**
 * ptc-tax-lifecycle's tax-returns list, filtered to one client (all statuses, the app's current
 * tax-prep year). `clientName` is the Dataverse account name shown on the appointment row.
 */
export function taxLifecycleClientReturnsUrl(clientName: string, baseUrl = TAX_LIFECYCLE_BASE_URL): string {
  const params = new URLSearchParams({
    view: 'all',
    'filter.clientName.eq': clientName,
  });
  return `${baseUrl}/tax-returns?${params.toString()}`;
}

/**
 * This app's Reviews list, filtered to one client — every review it has, open AND completed
 * (the `all` view is the engine default, so no `view` param is needed; `client` is the Reviews
 * column key and `eq` is case-insensitive — see `src/pages/ReviewsPage.tsx` `reviewColumns`).
 *
 * Carrying a `filter.*` param also suppresses the page's "My Active" fresh-landing seed, which only
 * fires for a visit with no filters — so the client scope survives the landing.
 */
export function clientReviewsPath(clientName: string): string {
  const params = new URLSearchParams({ 'filter.client.eq': clientName });
  return `/reviews?${params.toString()}`;
}
